import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { VideoMode } from '../types';
import { pillButtonSx, vb } from '../theme';

const OPTIONS: Array<{ mode: VideoMode; title: string; badge?: string; body: string }> = [
  {
    mode: 'presenter',
    title: 'Presenter',
    body: 'Elliott talks you through the lesson, with live captions. You review and edit the script. No brand kit needed.'
  },
  {
    mode: 'explainer',
    title: 'Explainer',
    badge: 'NEW',
    body: 'Line-art motion graphics, scene by scene, with Elliott narrating. You shape the storyboard.'
  }
];

// Prototype screen 2: "How should Elliott bring this lesson to life?"
const StyleStep: React.FC<{ lessonName: string; busy: boolean; onContinue: (mode: VideoMode) => void }> = ({
  lessonName,
  busy,
  onContinue
}) => {
  const [mode, setMode] = useState<VideoMode>('presenter');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography sx={{ fontFamily: vb.font, fontSize: 20, fontWeight: 800, color: vb.text }}>
          Generate a lesson video
        </Typography>
        <Typography sx={{ fontFamily: vb.font, fontSize: 14, color: vb.textSecondary, mt: 0.5 }}>
          How should Elliott bring{' '}
          <Box component="span" sx={{ color: vb.text, fontWeight: 700 }}>
            {lessonName}
          </Box>{' '}
          to life?
        </Typography>
      </Box>
      <Box
        role="radiogroup"
        aria-label="Video style"
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}
      >
        {OPTIONS.map((o) => {
          const selected = o.mode === mode;
          return (
            <Box
              key={o.mode}
              role="radio"
              aria-checked={selected}
              tabIndex={0}
              data-testid={`style-${o.mode}`}
              onClick={() => setMode(o.mode)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setMode(o.mode)}
              sx={{
                p: 2.25,
                borderRadius: '12px',
                cursor: 'pointer',
                border: `2px solid ${selected ? vb.brand : vb.border}`,
                background: selected ? 'rgba(247,148,32,0.06)' : vb.page,
                transition: 'border-color 0.15s ease',
                '&:hover': { borderColor: selected ? vb.brand : vb.borderStrong }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ fontFamily: vb.font, fontSize: 16, fontWeight: 800, color: vb.text }}>
                  {o.title}
                </Typography>
                {o.badge && (
                  <Box
                    sx={{
                      fontFamily: vb.font,
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: '0.1em',
                      px: 0.6,
                      py: 0.2,
                      borderRadius: '4px',
                      background: vb.brand,
                      color: '#1C1C1C'
                    }}
                  >
                    {o.badge}
                  </Box>
                )}
              </Box>
              <Typography sx={{ fontFamily: vb.font, fontSize: 13, color: vb.textSecondary, lineHeight: 1.5 }}>
                {o.body}
              </Typography>
              <Typography sx={{ fontFamily: vb.font, fontSize: 11.5, color: vb.textTertiary, mt: 1.25 }}>
                {o.mode === 'presenter' ? 'Elliott on screen · captions' : 'Animated · Elliott narrates'}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button data-testid="style-continue" disabled={busy} onClick={() => onContinue(mode)} sx={pillButtonSx(true)}>
          {busy ? 'Starting...' : 'Continue'}
        </Button>
      </Box>
    </Box>
  );
};

export default StyleStep;
